import { cn, Select, SelectItem, Typography } from "@kyan/ui";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";

import { routes } from "#/config/routes";

const createItems = (push: (href: string) => void) => [
  {
    id: "overview",
    label: "Overview",
    onClick: () => push(routes.portfolio.index),
  },
  {
    id: "history",
    label: "History",
    onClick: () => push(routes.portfolio.history),
  },
  {
    id: "settings",
    label: "Settings",
    onClick: () => push(routes.portfolio.settings),
  },
  {
    id: "api-keys",
    label: "API Keys",
    onClick: () => push(routes.portfolio.api),
  },
];

export const PortfolioMenu = () => {
  const router = useRouter();
  const pathname = usePathname();

  const [key, setKey] = useState(0);
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const items = createItems((href) => router.push(href));
  const isActive = Object.values(routes.portfolio).some((path) =>
    pathname.startsWith(path),
  );

  const openMenu = () => {
    clearTimeout(closeTimer.current);
    setOpen(true);
  };

  const scheduleClose = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 100);
  };

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  // Radix Select's DismissableLayer sets `pointer-events: none` on the body while
  // open, which makes the trigger instantly lose hover and fire a spurious
  // mouseleave — causing a flicker loop. Re-enable it so hover stays stable and
  // the rest of the header remains hoverable. The timeout runs after Radix's effect.
  useEffect(() => {
    if (!open) return;

    const id = setTimeout(() => {
      document.body.style.pointerEvents = "";
    }, 0);

    return () => clearTimeout(id);
  }, [open]);

  const hoverProps = { onMouseEnter: openMenu, onMouseLeave: scheduleClose };

  return (
    <Select
      key={key}
      open={open}
      value={undefined}
      variant="toolbar"
      iconClassName="hidden"
      contentClassName="w-44.5"
      contentProps={hoverProps}
      triggerProps={hoverProps}
      valueComponent={
        <Link
          href={routes.portfolio.index}
          className="flex h-full items-center"
        >
          <Typography className={cn({ "text-textPrimary": isActive })}>
            Portfolio
          </Typography>
        </Link>
      }
      triggerClassName={cn(
        "group aria-expanded:text-textPrimary aria-expanded:bg-background not-disabled:hover:bg-background hover:text-textPrimary h-full justify-start gap-0.5 border-x-0 border-b border-transparent px-4",
        {
          "border-primary": isActive,
        },
      )}
      onOpenChange={setOpen}
      onValueChange={(value) => {
        items.find(({ id }) => id === value)?.onClick?.();
        setOpen(false);
        setKey((previous) => previous + 1);
      }}
    >
      {items.map((item) => (
        <SelectItem
          key={item.id}
          value={item.id}
          className="bg-background dark:bg-buttonFill h-auto p-3 text-xs"
          itemComponent={
            <div className="group flex w-full cursor-pointer items-center justify-between">
              <Typography primary>{item.label}</Typography>
            </div>
          }
        />
      ))}
    </Select>
  );
};
